import { logError } from '@/utils/usefulFuncs';
import { AI_TOOLS } from './ai';

export const ADVANCED_SETTING_VALUE_TYPES = {
  date: 'date',
  string: 'string',
  bool: 'bool',
  onOff: 'onOff',
  integer: 'integer',
  float: 'float',
  custom: 'custom',
  stringList: 'stringList',
};

const DEFAULT_TTL_MS = 60_000;

const LOCAL_DEFINITIONS = {
  enableDailySummaries: {
    keyName: 'enableDailySummaries',
    valueType: ADVANCED_SETTING_VALUE_TYPES.bool,
    defaultValue: true,
    formDisplayName: 'Show Daily Summaries',
  },
  doc_list: {
    keyName: 'doc_list',
    valueType: ADVANCED_SETTING_VALUE_TYPES.stringList,
    defaultValue: undefined,
    formDisplayName: 'Doc List',
  },
  overrides: {
    keyName: 'overrides',
    valueType: ADVANCED_SETTING_VALUE_TYPES.stringList,
    defaultValue: undefined,
    formDisplayName: 'Overrides',
  },
};

const LOCAL_MODE_FIELDS = {
  [AI_TOOLS.LATEST_UPDATES_AND_NEWS]: {
    fields: ['enableDailySummaries'],
    startingValues: { enableDailySummaries: true },
  },
  [AI_TOOLS.AGENT_FLOW]: {
    fields: ['doc_list', 'overrides'],
    startingValues: { doc_list: [], overrides: [] },
  },
};

let LOADED_DEFINITIONS = {};
const MODE_CACHE = {};

function rebuildLoadedDefinitions() {
  const merged = { ...LOCAL_DEFINITIONS };
  Object.values(MODE_CACHE).forEach((entry) => {
    if (entry && entry.definitions) {
      Object.assign(merged, entry.definitions);
    }
  });
  LOADED_DEFINITIONS = merged;
}

rebuildLoadedDefinitions();

export const getAdvancedSettingsDefinitions = () => LOADED_DEFINITIONS;

export const setAdvancedSettingsDefinitions = (definitions) => {
  LOADED_DEFINITIONS = { ...LOCAL_DEFINITIONS, ...(definitions || {}) };
};

export const setAdvancedSettingsForMode = (mode, payload) => {
  if (!mode) return;
  const fieldsFromApi = Array.isArray(payload?.fields) ? payload.fields : [];
  const startingFromApi = payload?.startingValues || {};
  const definitionsFromApi = payload?.definitions || {};

  const local = LOCAL_MODE_FIELDS[mode];
  let mergedFields = fieldsFromApi;
  let mergedStarting = startingFromApi;
  if (local) {
    const seen = new Set(fieldsFromApi);
    const appended = local.fields.filter((f) => !seen.has(f));
    mergedFields = [...fieldsFromApi, ...appended];
    mergedStarting = { ...local.startingValues, ...startingFromApi };
    appended.forEach((f) => {
      if (!(f in mergedStarting) && f in local.startingValues) {
        mergedStarting[f] = local.startingValues[f];
      }
    });
  }

  MODE_CACHE[mode] = {
    fields: mergedFields,
    startingValues: mergedStarting,
    definitions: definitionsFromApi,
    fetchedAt: Date.now(),
  };
  rebuildLoadedDefinitions();
};

export const getAdvancedSettingsForMode = (mode) => MODE_CACHE[mode] || null;

export const hasFreshAdvancedSettingsForMode = (mode, ttlMs = DEFAULT_TTL_MS) => {
  const entry = MODE_CACHE[mode];
  if (!entry) return false;
  return Date.now() - entry.fetchedAt < ttlMs;
};

export const clearAdvancedSettingsCache = () => {
  Object.keys(MODE_CACHE).forEach((k) => delete MODE_CACHE[k]);
  rebuildLoadedDefinitions();
};

export const getToolStartingAdvancedSettingValues = (tool) => {
  const entry = MODE_CACHE[tool];
  if (!entry) {
    logError('No starting advanced setting values for tool', tool);
    return null;
  }
  return entry.startingValues;
};

export const getToolAdvancedSettingFields = (tool) => {
  const entry = MODE_CACHE[tool];
  if (!entry) {
    logError('No configurable advanced setting fields for tool', tool);
    return null;
  }
  return entry.fields;
};
